import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FiSearch,
  FiEye,
  FiCheck,
  FiX,
  FiDownload
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AdminBookings = () => {
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, pagination.page]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: 20
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const data = await api.getAdminBookings(params);
      setBookings(data.bookings || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await api.updateBookingStatus(bookingId, newStatus);
      setBookings(bookings.map(b =>
        b._id === bookingId ? { ...b, status: newStatus } : b
      ));
      toast.success(`Booking ${newStatus}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: { bg: '#E8F5E9', color: '#2E7D32' },
      pending: { bg: '#FFF8E1', color: '#F57C00' },
      'checked-in': { bg: '#E3F2FD', color: '#1976D2' },
      'checked-out': { bg: '#F5F5F5', color: '#616161' },
      cancelled: { bg: '#FFEBEE', color: '#C62828' }
    };
    return colors[status] || colors.pending;
  };

  const filteredBookings = bookings.filter(b =>
    b.bookingReference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.guestInfo?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.guestInfo?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.guestInfo?.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const styles = {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700'
    },
    exportBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      background: 'white',
      color: '#222',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    controls: {
      display: 'flex',
      gap: '16px',
      marginBottom: '24px',
      flexWrap: 'wrap'
    },
    searchWrapper: {
      flex: 1,
      minWidth: '250px',
      position: 'relative'
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#555'
    },
    searchInput: {
      width: '100%',
      padding: '12px 12px 12px 44px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem'
    },
    filters: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    },
    filterBtn: {
      padding: '10px 16px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      background: 'white',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '0.875rem'
    },
    filterActive: {
      background: '#222',
      color: 'white',
      borderColor: '#222'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '16px',
      borderBottom: '1px solid #eee',
      color: '#555',
      fontWeight: '500',
      fontSize: '0.875rem'
    },
    td: {
      padding: '16px',
      borderBottom: '1px solid #f5f5f5',
      verticalAlign: 'middle'
    },
    reference: {
      fontWeight: '600',
      color: '#1a3a5c'
    },
    guestName: {
      fontWeight: '500'
    },
    guestEmail: {
      color: '#555',
      fontSize: '0.8125rem'
    },
    badge: {
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '600'
    },
    actions: {
      display: 'flex',
      gap: '8px'
    },
    actionBtn: {
      padding: '6px 12px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.75rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      borderTop: '1px solid #eee'
    },
    pageInfo: {
      color: '#555',
      fontSize: '0.875rem'
    },
    pageButtons: {
      display: 'flex',
      gap: '8px'
    },
    pageBtn: {
      padding: '8px 16px',
      border: '1px solid #ddd',
      borderRadius: '6px',
      background: 'white',
      cursor: 'pointer'
    },
    empty: {
      textAlign: 'center',
      padding: '60px',
      color: '#555'
    }
  };

  const statusFilters = ['all', 'pending', 'confirmed', 'checked-in', 'checked-out', 'cancelled'];

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <header style={styles.header}>
        <h1 style={styles.title}>Bookings</h1>
        <button style={styles.exportBtn}>
          <FiDownload /> Export
        </button>
      </header>

      <div style={styles.controls}>
        <div style={styles.searchWrapper}>
          <FiSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by reference, guest name or email..."
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div style={styles.filters}>
          {statusFilters.map(status => (
            <button
              key={status}
              style={{
                ...styles.filterBtn,
                ...(statusFilter === status ? styles.filterActive : {})
              }}
              onClick={() => {
                setStatusFilter(status);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.card}>
        {filteredBookings.length > 0 ? (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Reference</th>
                  <th style={styles.th}>Guest</th>
                  <th style={styles.th}>Apartment</th>
                  <th style={styles.th}>Check-in</th>
                  <th style={styles.th}>Check-out</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map(booking => {
                  const statusStyle = getStatusColor(booking.status);
                  return (
                    <tr key={booking._id}>
                      <td style={styles.td}>
                        <Link
                          to={`/admin/bookings/${booking._id}`}
                          style={styles.reference}
                        >
                          {booking.bookingReference}
                        </Link>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.guestName}>
                          {booking.guestInfo?.firstName} {booking.guestInfo?.lastName}
                        </div>
                        <div style={styles.guestEmail}>{booking.guestInfo?.email}</div>
                      </td>
                      <td style={styles.td}>{booking.apartment?.name}</td>
                      <td style={styles.td}>
                        {new Date(booking.checkIn).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>
                      <td style={styles.td}>
                        {new Date(booking.checkOut).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>
                      <td style={styles.td}>
                        £{booking.pricing?.totalPrice?.toFixed(2)}
                      </td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.badge,
                            background: statusStyle.bg,
                            color: statusStyle.color
                          }}
                        >
                          {booking.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actions}>
                          <Link
                            to={`/admin/bookings/${booking._id}`}
                            style={{
                              ...styles.actionBtn,
                              background: '#f5f5f5',
                              color: '#222'
                            }}
                          >
                            <FiEye /> View
                          </Link>
                          {booking.status === 'pending' && (
                            <>
                              <button
                                style={{
                                  ...styles.actionBtn,
                                  background: '#E8F5E9',
                                  color: '#2E7D32'
                                }}
                                onClick={() => updateStatus(booking._id, 'confirmed')}
                              >
                                <FiCheck /> Confirm
                              </button>
                              <button
                                style={{
                                  ...styles.actionBtn,
                                  background: '#FFEBEE',
                                  color: '#C62828'
                                }}
                                onClick={() => updateStatus(booking._id, 'cancelled')}
                              >
                                <FiX /> Cancel
                              </button>
                            </>
                          )}
                          {booking.status === 'confirmed' && (
                            <button
                              style={{
                                ...styles.actionBtn,
                                background: '#E3F2FD',
                                color: '#1976D2'
                              }}
                              onClick={() => updateStatus(booking._id, 'checked-in')}
                            >
                              Check In
                            </button>
                          )}
                          {booking.status === 'checked-in' && (
                            <button
                              style={{
                                ...styles.actionBtn,
                                background: '#F5F5F5',
                                color: '#616161'
                              }}
                              onClick={() => updateStatus(booking._id, 'checked-out')}
                            >
                              Check Out
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {pagination.pages > 1 && (
              <div style={styles.pagination}>
                <div style={styles.pageInfo}>
                  Showing {(pagination.page - 1) * 20 + 1} - {Math.min(pagination.page * 20, pagination.total)} of {pagination.total}
                </div>
                <div style={styles.pageButtons}>
                  <button
                    style={styles.pageBtn}
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </button>
                  <button
                    style={styles.pageBtn}
                    disabled={pagination.page === pagination.pages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={styles.empty}>
            <p>No bookings found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookings;
